import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type {
  AppNotification,
  UnreadCountResponse,
} from "../../../types/notifications";
import { adjustSidebarUnreadCount } from "../../../utils/notificationBadges";
import {
  getNotificationsApi,
  getUnreadCountApi,
  markNotificationReadApi,
  markAllReadApi,
  dismissNotificationApi,
} from "../../../Apis/notifications";
import { isLocalExportNotificationId } from "../../../utils/buildExportCompletionNotification";

const PAGE_SIZE = 20;

interface NotificationsState {
  items: AppNotification[];
  unreadCount: { total: number; MY_LEADS: number; MY_TASKS: number };
  isDrawerOpen: boolean;
  isLoading: boolean;
  hasMore: boolean;
  cursor?: string; // createdAt of last fetched item (for cursor pagination)
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: { total: 0, MY_LEADS: 0, MY_TASKS: 0 },
  isDrawerOpen: false,
  isLoading: false,
  hasMore: true,
};

// ── Thunks ──────────────────────────────────────────────────────────────────

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getUnreadCountApi();
      return res.data as UnreadCountResponse;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch unread count"
      );
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (
    { reset = false }: { reset?: boolean },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = (getState() as any).notifications as NotificationsState;
      const params = reset
        ? { limit: PAGE_SIZE }
        : { limit: PAGE_SIZE, before: state.cursor };
      const res = await getNotificationsApi(params);
      return {
        notifications: res.data.notifications,
        hasMore: res.data.hasMore,
        reset,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch notifications"
      );
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      if (isLocalExportNotificationId(id)) {
        dispatch(markReadLocally(id));
        return;
      }
      await markNotificationReadApi(id);
      dispatch(markReadLocally(id));
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to mark as read");
    }
  }
);

export const markAllRead = createAsyncThunk(
  "notifications/markAllRead",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await markAllReadApi();
      dispatch(markAllReadLocally());
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to mark all as read"
      );
    }
  }
);

export const dismissNotification = createAsyncThunk(
  "notifications/dismiss",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      if (isLocalExportNotificationId(id)) {
        dispatch(removeNotification(id));
        return;
      }
      await dismissNotificationApi(id);
      dispatch(removeNotification(id));
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to dismiss notification"
      );
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    openDrawer(state) {
      state.isDrawerOpen = true;
    },
    closeDrawer(state) {
      state.isDrawerOpen = false;
    },

    clearNotifications: () => initialState,

    // Called by socket "notification:new" event
    addNotification(state, action: PayloadAction<AppNotification>) {
      const notif = action.payload;
      // Prepend — newest first
      state.items.unshift(notif);
      // Increment unread counts (optional chaining guards against stale persisted state shapes)
      if (!notif.readAt) {
        if (!state.unreadCount) {
          state.unreadCount = { total: 0, MY_LEADS: 0, MY_TASKS: 0 };
        }
        state.unreadCount.total += 1;
        adjustSidebarUnreadCount(
          state.unreadCount,
          {
            templateKey: notif.templateKey,
            sidebarBadgeKey: notif.surfaces?.sidebarBadgeKey,
          },
          1
        );
      }
    },

    // Called by socket "notification:read" event or after markNotificationRead thunk
    markReadLocally(state, action: PayloadAction<string>) {
      const id = action.payload;
      const item = state.items.find((n) => n._id === id);
      if (item && !item.readAt) {
        item.readAt = new Date().toISOString();
        state.unreadCount.total = Math.max(0, state.unreadCount.total - 1);
        adjustSidebarUnreadCount(
          state.unreadCount,
          {
            templateKey: item.templateKey,
            sidebarBadgeKey: item.surfaces?.sidebarBadgeKey,
          },
          -1
        );
      }
    },

    // Called by socket "notification:read-all" event or after markAllRead thunk
    markAllReadLocally(state) {
      const now = new Date().toISOString();
      state.items.forEach((item) => {
        if (!item.readAt) item.readAt = now;
      });
      state.unreadCount = { total: 0, MY_LEADS: 0, MY_TASKS: 0 };
    },

    // Called by socket "notification:aggregated" event
    updateAggregated(
      state,
      action: PayloadAction<{
        notificationId: string;
        aggregationCount: number;
        title: string;
      }>
    ) {
      const { notificationId, aggregationCount, title } = action.payload;
      const item = state.items.find((n) => n._id === notificationId);
      if (item) {
        item.aggregationCount = aggregationCount;
        item.title = title;
      }
    },

    // Called after dismissNotification thunk
    removeNotification(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.items.findIndex((n) => n._id === id);
      if (idx !== -1) {
        const item = state.items[idx];
        if (!item.readAt) {
          state.unreadCount.total = Math.max(0, state.unreadCount.total - 1);
          adjustSidebarUnreadCount(
            state.unreadCount,
            {
              templateKey: item.templateKey,
              sidebarBadgeKey: item.surfaces?.sidebarBadgeKey,
            },
            -1
          );
        }
        state.items.splice(idx, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUnreadCount
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        const data = action.payload as UnreadCountResponse;
        state.unreadCount = {
          total: data.total ?? 0,
          MY_LEADS: data.byBadgeKey?.MY_LEADS ?? 0,
          MY_TASKS: data.byBadgeKey?.MY_TASKS ?? 0,
        };
      })

      // fetchNotifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        const { notifications, hasMore, reset } = action.payload as {
          notifications: AppNotification[];
          hasMore: boolean;
          reset: boolean;
        };
        if (reset) {
          const localExportItems = state.items.filter((item) =>
            isLocalExportNotificationId(item._id)
          );
          const serverEntityIds = new Set(
            notifications.map((item) => item.entityId)
          );
          state.items = [
            ...localExportItems.filter(
              (item) => !serverEntityIds.has(item.entityId)
            ),
            ...notifications,
          ];
        } else {
          state.items.push(...notifications);
        }
        state.hasMore = hasMore;
        if (notifications.length > 0) {
          state.cursor = notifications[notifications.length - 1].createdAt;
        }
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  openDrawer,
  closeDrawer,
  clearNotifications,
  addNotification,
  markReadLocally,
  markAllReadLocally,
  updateAggregated,
  removeNotification,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
